import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from "firebase/firestore";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove
} from "@dnd-kit/sortable";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { logActivity } from "../activityLog";
import { fnUrl } from "../../../utils/fnUrl";
import SortableBlock from "./blocks/SortableBlock";
import BlockWrapper from "./blocks/BlockWrapper";
import { db } from "../utils/firebase";
import { LOCALES, type Locale, TARGET_LOCALES, LOCALE_FLAGS, hasLocaleContent, deloc } from "../utils/locale";
import { parsePath, createEmptyBlock, getBlockPreview, BlockIcon } from "../utils/blocks";
import { extractTexts, injectTranslations } from "./TranslationEngine";
import { PreviewImage, MetaThumb } from "./PreviewImage";
import SimpleBlock from "./SimpleBlock";

/** Draggable palette item */
const DraggablePaletteItem: React.FC<{ type: string; label: string }> = ({ type, label }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `palette-${type}` });
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`editor__palette-item ${type === "group" ? "editor__palette-item--group" : ""}`}
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <span className="editor__palette-icon"><BlockIcon type={type} /></span>
      <span className="editor__palette-label">{label}</span>
    </button>
  );
};

/** Droppable zone wrapping the blocks area */
const BlocksDropZone: React.FC<{ children: React.ReactNode; active?: boolean }> = ({ children, active }) => {
  const { setNodeRef } = useDroppable({ id: "editor-drop-zone" });
  return (
    <div ref={setNodeRef} className={`editor__blocks${active ? " editor__blocks--drop-active" : ""}`}>
      {children}
    </div>
  );
};

/** Droppable gap between root blocks */
const DropGap: React.FC<{ id: string }> = ({ id }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} className={`editor__drop-gap${isOver ? " editor__drop-gap--active" : ""}`} />;
};

const Editor = () => {
  const { project, contentType, docId } = useParams<{
    project: string;
    contentType: string;
    docId: string;
  }>();
  const navigate = useNavigate();
  const { user, firebaseUser } = useAuth();

  const [docData, setDocData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locale, setLocale] = useState<Locale>("en");
  const [dirty, setDirty] = useState(false);
  const [originalUpdatedAt, setOriginalUpdatedAt] = useState<Timestamp | null>(null);
  const [activeBlock, setActiveBlock] = useState<any>(null);
  const [hoverGroupPath, setHoverGroupPath] = useState<string | null>(null);
  const [hoverRoot, setHoverRoot] = useState(false);
  const [viewMode, setViewMode] = useState<"preview" | "editor">(
    contentType === "insights" || contentType === "recipes" || contentType === "workouts" ? "preview" : "editor"
  );
  const [translating, setTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] = useState(0);
  const [translateTotal, setTranslateTotal] = useState(0);
  const [pushing, setPushing] = useState(false);
  const [pushTime, setPushTime] = useState("");
  const [pushEnv, setPushEnv] = useState<"dev" | "prod">("dev");
  const [showPushTime, setShowPushTime] = useState(false);


  const [newMetaKey, setNewMetaKey] = useState("");
  const [blockConfig, setBlockConfig] = useState<any[] | null>(null);
  const [categories, setCategories] = useState<any[] | null>(null);
  const [recipeOptions, setRecipeOptions] = useState<any>(null);
  const [containsList, setContainsList] = useState<string[]>([]);

  const allSubcategoryIds = useMemo(
    () => new Set((categories || []).flatMap((cat: any) => (Array.isArray(cat.subcategories) ? cat.subcategories.map((sub: any) => sub.id) : []))),
    [categories]
  );

  const categorySelectOptions = useMemo(
    () => (categories || []).flatMap((cat: any) => {
      const parent = [{ value: cat.id, label: `${cat.emoji || ""} ${deloc(cat.title)}`.trim() }];
      const subOptions = (Array.isArray(cat.subcategories) ? cat.subcategories : []).map((sub: any) => ({
        value: `${cat.id}::${sub.id}`,
        label: `${cat.emoji || ""} ${deloc(cat.title)} - ${deloc(sub.title)}`.trim(),
      }));
      return [...parent, ...subOptions];
    }),
    [categories]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const loadDoc = async () => {
    if (!project || !contentType || !docId) return;
    setLoading(true);

    try {
      const snap = await getDoc(doc(db, "content", project, contentType, docId));
      if (!snap.exists()) {
        setDocData(null);
        setLoading(false);
        return;
      }

      const data = snap.data();
      setDocData(data);
      setOriginalUpdatedAt(data.updatedAt || null);
      setDirty(false);
    } catch (err) {
      console.error("[Editor] Error loading:", err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadDoc();
    setLocale("en");

  }, [project, contentType, docId]);

  // Fetch block config for dynamic palette
  useEffect(() => {
    if (!project) return;
    getDoc(doc(db, "content", project, "_config", "blocks"))
      .then((snap) => {
        if (snap.exists()) {
          setBlockConfig(snap.data().blocks || []);
        } else {
          setBlockConfig([]);
        }
      })
      .catch(() => setBlockConfig(null));
    if (contentType) {
      if (contentType === "recipes") {
        Promise.all([
          getDoc(doc(db, "content", project, "_config", "recipes")),
          getDoc(doc(db, "content", project, "_config", "recipe_meta")),
        ])
          .then(([recipesSnap, metaSnap]) => {
            const recipesData = recipesSnap.exists() ? recipesSnap.data() : {};
            const metaData = metaSnap.exists() ? metaSnap.data() : {};
            setCategories(recipesData.categories || []);
            setRecipeOptions({
              ...recipesData,
              ...metaData,
              meal_roles: metaData.meal_roles || recipesData.meal_roles || [],
              difficulties: metaData.difficulties || recipesData.difficulties || [],
            });
          })
          .catch(() => {
            setCategories([]);
            setRecipeOptions(null);
          });
      } else {
        getDoc(doc(db, "content", project, "_config", contentType))
          .then((snap) => {
            if (snap.exists()) {
              const data = snap.data();
              setCategories(data.categories || []);
            } else {
              setCategories([]);
            }
          })
          .catch(() => setCategories([]));
      }
    }
  }, [project, contentType]);

  // Resolve "Contains" from ingredient documents for recipes
  useEffect(() => {
    if (!docData?.ingredients_raw || contentType !== "recipes") {
      setContainsList([]);
      return;
    }
    const ids = new Set<string>();
    for (const group of docData.ingredients_raw) {
      for (const item of group.items || []) {
        if (item.canonical_id) ids.add(item.canonical_id);
      }
    }
    if (ids.size === 0) { setContainsList([]); return; }

    Promise.all(
      Array.from(ids).map(id =>
        getDoc(doc(db, "ingredients", id))
          .then(snap => {
            if (!snap.exists()) return [];
            const ingredient = snap.data() || {};
            const names = ingredient.name || {};
            const value = names[locale] || names.en || "";
            if (!value) return [];
            return [String(value)];
          })
          .catch(() => [])
      )
    ).then(results => {
      const all = new Set<string>();
      for (const arr of results) {
        for (const item of arr) all.add(item);
      }
      setContainsList(Array.from(all).sort());
    });
  }, [docData?.ingredients_raw, contentType, locale]);

  const updateField = useCallback(
    (path: string, value: any) => {
      setDocData((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev));
        const keys = path.split(".");
        let obj = next;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!obj[keys[i]]) obj[keys[i]] = {};
          obj = obj[keys[i]];
        }
        obj[keys[keys.length - 1]] = value;
        return next;
      });
      setDirty(true);
  
    },
    []
  );

  const selectedPrimaryCategoryValue = useMemo(() => {
    if (!docData?.meta?.category) return "";
    const currentCategory = docData.meta.category;
    const currentTags = Array.isArray(docData.meta.tags) ? docData.meta.tags : [];
    const parent = (categories || []).find((cat: any) => cat.id === currentCategory);
    const activeSub = (Array.isArray(parent?.subcategories) ? parent.subcategories : []).find((sub: any) => currentTags.includes(sub.id));
    return activeSub ? `${currentCategory}::${activeSub.id}` : currentCategory;
  }, [categories, docData]);

  const handlePrimaryCategoryChange = useCallback((value: string) => {
    const currentTags = Array.isArray(docData?.meta?.tags) ? docData.meta.tags : [];
    const tagsWithoutSubcats = currentTags.filter((tag: string) => !allSubcategoryIds.has(tag));

    if (!value) {
      updateField("meta.category", "");
      updateField("meta.tags", tagsWithoutSubcats);
      return;
    }

    if (value.includes("::")) {
      const [parentId, subId] = value.split("::");
      updateField("meta.category", parentId);
      updateField("meta.tags", Array.from(new Set([...tagsWithoutSubcats.filter((tag: string) => tag !== parentId), subId])));
      return;
    }

    updateField("meta.category", value);
    updateField("meta.tags", tagsWithoutSubcats.filter((tag: string) => tag !== value));
  }, [allSubcategoryIds, docData, updateField]);

  const getContent = (): any[] => {
    if (!docData) return [];
    return docData.content || [];
  };

  const setContent = (newContent: any[]) => {
    updateField("content", newContent);
  };

  const updateBlock = (index: number, newBlock: any) => {
    const content = [...getContent()];
    content[index] = newBlock;
    setContent(content);
  };

  const deleteBlock = (index: number) => {
    const content = [...getContent()];
    content.splice(index, 1);
    setContent(content);
  };

  const addBlock = (type: string, afterIndex?: number) => {
    const newBlock = createEmptyBlock(type, blockConfig || undefined);
    const content = [...getContent()];
    const insertAt = afterIndex !== undefined ? afterIndex + 1 : content.length;
    content.splice(insertAt, 0, newBlock);
    setContent(content);
  };

  // Drag & drop — path-based IDs for cross-container support
  const rootIds = useMemo(() => {
    return getContent().map((_, i) => `blk-${i}`);
  }, [docData, contentType]);

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    if (id.startsWith("palette-")) {
      // Dragging from palette — show block type in overlay
      setActiveBlock({ type: id.replace("palette-", "") } as any);
      return;
    }
    const path = parsePath(id);
    const c = getContent();
    let block: any = c[path[0]];
    for (let i = 1; i < path.length; i++) block = block?.content?.[path[i]];
    setActiveBlock(block || null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const overId = event.over?.id as string | undefined;
    if (!overId) { setHoverGroupPath(null); setHoverRoot(false); return; }

    // Over a gap inside a group
    if (overId.startsWith("ggap-")) {
      const parts = overId.replace("ggap-", "").split("-").map(Number);
      setHoverGroupPath(parts.slice(0, -1).join("-"));
      setHoverRoot(false);
      return;
    }
    // Over a gap between root blocks
    if (overId.startsWith("gap-")) {
      setHoverGroupPath(null);
      setHoverRoot(true);
      return;
    }
    // Over a group drop zone directly
    if (overId.startsWith("group-")) {
      setHoverGroupPath(overId.replace("group-", ""));
      setHoverRoot(false);
      return;
    }
    // Over a child block inside a group (blk-2-1 means group index 2)
    if (overId.startsWith("blk-")) {
      const path = parsePath(overId);
      if (path.length >= 2) {
        setHoverGroupPath(path.slice(0, -1).join("-")); // closest parent group
        setHoverRoot(false);
        return;
      }
    }
    // Over root-level target
    setHoverGroupPath(null);
    setHoverRoot(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveBlock(null);
    setHoverGroupPath(null);
    setHoverRoot(false);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Helper: is the target inside a group?
    const isTargetInsideGroup = (id: string) =>
      id.startsWith("group-") || (id.startsWith("blk-") && parsePath(id).length >= 2);

    // Palette drop — create new block at target position
    if (activeId.startsWith("palette-")) {
      const blockType = activeId.replace("palette-", "");



      const newBlock = createEmptyBlock(blockType, blockConfig || undefined);
      const newContent: any[] = JSON.parse(JSON.stringify(getContent()));

      if (overId === "editor-drop-zone") {
        newContent.push(newBlock);
      } else if (overId.startsWith("ggap-")) {
        const parts = overId.replace("ggap-", "").split("-").map(Number);
        const groupPath = parts.slice(0, -1);
        const insertAt = parts[parts.length - 1];
        let target: any = newContent[groupPath[0]];
        for (let i = 1; i < groupPath.length; i++) target = target.content[groupPath[i]];
        if (!target.content) target.content = [];
        target.content.splice(insertAt, 0, newBlock);
      } else if (overId.startsWith("gap-")) {
        const gapIndex = parseInt(overId.replace("gap-", ""));
        newContent.splice(gapIndex, 0, newBlock);
      } else if (overId.startsWith("group-")) {
        const groupPath = overId.replace("group-", "").split("-").map(Number);
        let target: any = newContent;
        target = target[groupPath[0]];
        for (let i = 1; i < groupPath.length; i++) target = target.content[groupPath[i]];
        if (!target.content) target.content = [];
        target.content.push(newBlock);
      } else {
        const toPath = parsePath(overId);
        if (toPath.length === 1) {
          newContent.splice(toPath[0] + 1, 0, newBlock);
        } else {
          if (!newContent[toPath[0]].content) newContent[toPath[0]].content = [];
          newContent[toPath[0]].content.splice(toPath[1] + 1, 0, newBlock);
        }
      }
      setContent(newContent);
      return;
    }

    // Reorder existing blocks
    if (activeId === overId) return;

    // If dropped on a gap, insert at that root position
    if (overId.startsWith("gap-")) {
      const gapIndex = parseInt(overId.replace("gap-", ""));
      const fromPath = parsePath(activeId);
      const newContent: any[] = JSON.parse(JSON.stringify(getContent()));
      const getParent = (root: any[], path: number[]): any[] => {
        let arr = root;
        for (let i = 0; i < path.length - 1; i++) arr = arr[path[i]].content;
        return arr;
      };
      const fromParent = getParent(newContent, fromPath);
      const movedBlock = fromParent.splice(fromPath[fromPath.length - 1], 1)[0];
      const adj = fromPath.length === 1 && fromPath[0] < gapIndex ? gapIndex - 1 : gapIndex;
      newContent.splice(adj, 0, movedBlock);
      setContent(newContent);
      return;
    }

    // If dropped on a gap inside a group
    if (overId.startsWith("ggap-")) {
      const parts = overId.replace("ggap-", "").split("-").map(Number);
      const groupPath = parts.slice(0, -1);
      const insertAt = parts[parts.length - 1];
      const fromPath = parsePath(activeId);
      const newContent: any[] = JSON.parse(JSON.stringify(getContent()));
      const getParent = (root: any[], path: number[]): any[] => {
        let arr = root;
        for (let i = 0; i < path.length - 1; i++) arr = arr[path[i]].content;
        return arr;
      };
      const fromParent = getParent(newContent, fromPath);
      const movedBlock = fromParent.splice(fromPath[fromPath.length - 1], 1)[0];
      if (fromPath.length === 1 && fromPath[0] < groupPath[0]) groupPath[0]--;
      let target: any = newContent[groupPath[0]];
      for (let i = 1; i < groupPath.length; i++) target = target.content[groupPath[i]];
      if (!target.content) target.content = [];
      let adj = insertAt;
      const fromParentPath = fromPath.slice(0, -1);
      if (fromParentPath.join("-") === groupPath.join("-") && fromPath[fromPath.length - 1] < insertAt) adj--;
      target.content.splice(adj, 0, movedBlock);
      setContent(newContent);
      return;
    }

    // If dropped on a group droppable zone (empty group), append into it
    if (overId.startsWith("group-")) {
      const groupPath = overId.replace("group-", "").split("-").map(Number);
      const fromPath = parsePath(activeId);
      const newContent: any[] = JSON.parse(JSON.stringify(getContent()));
      const getParent = (root: any[], path: number[]): any[] => {
        let arr = root;
        for (let i = 0; i < path.length - 1; i++) arr = arr[path[i]].content;
        return arr;
      };
      const fromParent = getParent(newContent, fromPath);
      const movedBlock = fromParent.splice(fromPath[fromPath.length - 1], 1)[0];
      if (fromPath.length === 1 && fromPath[0] < groupPath[0]) groupPath[0]--;
      let target: any = newContent[groupPath[0]];
      for (let i = 1; i < groupPath.length; i++) target = target.content[groupPath[i]];
      if (!target.content) target.content = [];
      target.content.push(movedBlock);
      setContent(newContent);
      return;
    }

    const fromPath = parsePath(activeId);
    const toPath = parsePath(overId);
    const c = getContent();

    const newContent: any[] = JSON.parse(JSON.stringify(c));

    // Navigate to parent array at any depth
    const getParent = (root: any[], path: number[]): any[] => {
      let arr = root;
      for (let i = 0; i < path.length - 1; i++) {
        arr = arr[path[i]].content;
      }
      return arr;
    };

    // Extract the block from source
    const fromParent = getParent(newContent, fromPath);
    const fromIdx = fromPath[fromPath.length - 1];
    const movedBlock = fromParent.splice(fromIdx, 1)[0];

    // Insert at target (re-navigate after removal since indices may have shifted)
    const toParent = getParent(newContent, toPath);
    let toIdx = toPath[toPath.length - 1];

    // Adjust if same parent and source was before target
    const fromParentPath = fromPath.slice(0, -1).join(",");
    const toParentPath = toPath.slice(0, -1).join(",");
    if (fromParentPath === toParentPath && fromIdx < toIdx) {
      toIdx--;
    }

    toParent.splice(toIdx, 0, movedBlock);
    setContent(newContent);
  };

  // Meta field management — canonical order
  const META_ORDER = ["date", "category", "tags"];
  const metaKeys = docData?.meta
    ? [
        ...META_ORDER.filter((k) => k in docData.meta),
        ...Object.keys(docData.meta).filter((k) => !META_ORDER.includes(k)),
      ]
    : [];

  const addMetaField = () => {
    if (!newMetaKey.trim()) return;
    const key = newMetaKey.trim().toLowerCase().replace(/\s+/g, "_");
    updateField(`meta.${key}`, "");
    setNewMetaKey("");
  };

  const removeMetaField = (key: string) => {
    setDocData((prev: any) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (next.meta) delete next.meta[key];
      return next;
    });
    setDirty(true);

  };

  const save = async () => {
    if (!project || !contentType || !docId || !docData) return;

    // Validate date field if present
    if (docData.meta?.date) {
      const d = docData.meta.date;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || isNaN(new Date(d).getTime())) {
        alert(`Invalid date "${d}". Use format YYYY-MM-DD.`);
        return;
      }
    }

    setSaving(true);

    try {
      const freshSnap = await getDoc(doc(db, "content", project, contentType, docId));
      if (freshSnap.exists()) {
        const freshData = freshSnap.data();
        if (
          originalUpdatedAt &&
          freshData.updatedAt &&
          freshData.updatedAt.seconds !== originalUpdatedAt.seconds
        ) {
          const overwrite = window.confirm(
            `This document was modified by ${freshData.updatedBy || "someone"} since you opened it. Overwrite?`
          );
          if (!overwrite) {
            setSaving(false);
            return;
          }
        }
      }

      const updateData: any = {
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || "unknown"
      };

      if (docData.content) updateData.content = docData.content;
      if (docData.meta) updateData.meta = docData.meta;
      if (docData.images) updateData.images = docData.images;
      if (docData.preview) updateData.preview = docData.preview;

      await updateDoc(doc(db, "content", project, contentType, docId), updateData);
      toast.success("Saved");
      setDirty(false);

      const updated = await getDoc(doc(db, "content", project, contentType, docId));
      if (updated.exists()) {
        setOriginalUpdatedAt(updated.data().updatedAt || null);
      }

      // Log edit activity
      const titleBlock = (docData.content || []).find((b: any) => b.type === "title");
      const docTitle = titleBlock?.text?.en || titleBlock?.text || docId;
      logActivity(project, {
        action: "edit",
        contentType,
        docId,
        docTitle: typeof docTitle === "string" ? docTitle : docId,
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err: any) {
      console.error("[Editor] Save error:", err);
      toast.error("Error saving");
    }

    setSaving(false);
  };

  /** Auto-save only the image URL at a given block path — reads fresh data from Firestore */
  const autoSaveImageUrl = async (blockPath: number[], newUrl: string) => {
    if (!project || !contentType || !docId) return;
    try {
      const snap = await getDoc(doc(db, "content", project, contentType, docId));
      if (!snap.exists()) return;
      const data = snap.data();
      const contentKey = "content";
      const freshContent = JSON.parse(JSON.stringify(data[contentKey] || []));
      let target = freshContent[blockPath[0]];
      for (let i = 1; i < blockPath.length; i++) {
        target = target?.content?.[blockPath[i]];
      }
      if (target) {
        target.url = newUrl;
        await updateDoc(doc(db, "content", project, contentType, docId), {
          [contentKey]: freshContent,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        });
      }
    } catch (err) {
      console.error("[Editor] Auto-save image error:", err);
    }
  };

  const deleteDocument = async () => {
    if (!project || !contentType || !docId) return;
    if (!window.confirm(`Delete "${docId}" permanently? This cannot be undone.`)) return;
    try {
      const titleBlock = (docData?.content || []).find((b: any) => b.type === "title");
      const docTitle = titleBlock?.text?.en || titleBlock?.text || docId;
      await deleteDoc(doc(db, "content", project, contentType, docId));
      logActivity(project, {
        action: "delete",
        contentType,
        docId,
        docTitle: typeof docTitle === "string" ? docTitle : docId,
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
      navigate(`/cms/${project}/${contentType}`);
    } catch (err) {
      console.error("[Editor] Delete error:", err);
      alert("Failed to delete document");
    }
  };

  const handleSendPushClick = () => {
    if (!project || !contentType || !docId || !firebaseUser) return;
    setShowPushTime(true);
  };

  const handlePushCancel = () => {
    setShowPushTime(false);
    setPushTime("");
    setPushEnv("dev");
  };

  const handlePushConfirm = async () => {
    if (!project || !contentType || !docId || !firebaseUser || !pushTime) return;

    setShowPushTime(false);
    setPushing(true);
    try {
      const idToken = await firebaseUser.getIdToken();
      const res = await fetch(fnUrl("cmsSendPush"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ project, docId, contentType, scheduleAt: pushTime, env: pushEnv }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      const sent = data.successCount ?? data.sent ?? 0;
      const failed = data.failureCount ?? data.failed ?? 0;
      const detail = failed > 0
        ? `Sent: ${sent}, failed: ${failed}`
        : `Sent to ${sent} users`;
      toast.success(detail);

      logActivity(project, {
        action: "push_sent",
        contentType,
        docId,
        detail,
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err: any) {
      console.error("[Editor] Send push error:", err);
      toast.error(`Push failed: ${err.message || "Unknown error"}`);
    } finally {
      setPushing(false);
      setPushTime("");
    }
  };

  const handleTranslate = async () => {
    if (!project || !contentType || !docId || !firebaseUser) return;
    const isSpecific = locale !== "en";
    const targetLocales = isSpecific ? [locale] : [...TARGET_LOCALES];
    const force = isSpecific; // when targeting a specific locale, always retranslate it
    const blocks = docData?.content || [];
    const extracted = extractTexts(blocks, "", force);
    if (extracted.length === 0) { alert("No translatable text found."); return; }

    // Build task list: short texts → 1 call for all locales; long texts → 1 call per locale
    const SHORT_LIMIT = 250;
    interface TranslateTask { key: string; enText: string; locales: string[] }
    const tasks: TranslateTask[] = extracted.flatMap(({ key, enText }) =>
      enText.length > SHORT_LIMIT
        ? targetLocales.map((l) => ({ key, enText, locales: [l] }))
        : [{ key, enText, locales: targetLocales }]
    );

    setTranslating(true);
    setTranslateProgress(0);
    setTranslateTotal(tasks.length);

    let idToken: string;
    try {
      idToken = await firebaseUser.getIdToken();
    } catch {
      setTranslating(false);
      alert("Authentication error. Please sign in again.");
      return;
    }

    const url = fnUrl("translateContent");
    const translationMap = new Map<string, Record<string, string>>();
    let completed = 0;

    const MAX_CONCURRENT = 5;
    for (let i = 0; i < tasks.length; i += MAX_CONCURRENT) {
      const chunk = tasks.slice(i, i + MAX_CONCURRENT);
      await Promise.all(
        chunk.map(async ({ key, enText, locales }) => {
          try {
            const res = await fetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${idToken}`,
              },
              body: JSON.stringify({ key, text: enText, targetLocales: locales }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            // Merge results for keys that are split across multiple calls
            translationMap.set(key, { ...(translationMap.get(key) ?? {}), ...data.locales });
          } catch (err) {
            console.error(`[translate] key="${key}":`, err);
          }
          completed++;
          setTranslateProgress(completed);
        })
      );
    }

    if (translationMap.size === 0) {
      setTranslating(false);
      alert("Translation failed. Check console for errors.");
      return;
    }

    const updatedBlocks = injectTranslations(blocks, translationMap);
    try {
      await updateDoc(doc(db, "content", project, contentType, docId), {
        content: updatedBlocks,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || "unknown",
      });
      setDocData((prev: any) => ({ ...prev, content: updatedBlocks }));
      setDirty(false);
      toast.success("✅ Translated & saved");
      const titleBlock = (updatedBlocks || []).find((b: any) => b.type === "title");
      const docTitle = titleBlock?.text?.en || titleBlock?.text || docId;
      logActivity(project, {
        action: "translate",
        contentType,
        docId,
        docTitle: typeof docTitle === "string" ? docTitle : docId,
        detail: `Translated ${translationMap.size} fields → ${targetLocales.join(", ")}`,
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err) {
      console.error("[Editor] Failed to save translations:", err);
      alert("Translations done but failed to save. Please try again.");
    }

    setTranslating(false);
  };

  if (loading || blockConfig === null || categories === null) {
    return <div className="editor__loading">Loading...</div>;
  }

  if (!docData) {
    return <div className="editor__empty">Document not found.</div>;
  }

  const content = getContent();

  return (
    <div className="editor">
      {/* Header */}
      <div className="editor__header">
        <button className="editor__back" onClick={() => navigate(`/cms/${project}/${contentType}`)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>

        <span className="editor__doc-id">{docId}</span>

        <div className="editor__mode-toggle">
          <button className={`editor__mode-btn${viewMode === "editor" ? " editor__mode-btn--active" : ""}`} onClick={() => setViewMode("editor")}>Editor</button>
          <button className={`editor__mode-btn${viewMode === "preview" ? " editor__mode-btn--active" : ""}`} onClick={() => setViewMode("preview")}>Preview</button>
          <button className="editor__mode-btn" onClick={async () => {
            try {
              const url = `${fnUrl("exportContent")}?project=${project}&contentType=${contentType}&id=${docId}&locale=${locale}`;
              const res = await fetch(url);
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const json = await res.json();
              const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
              window.open(URL.createObjectURL(blob), "_blank");
            } catch (err) {
              console.error("[Editor] Export error:", err);
              alert("Export failed. Make sure functions emulator is running.");
            }
          }}>JSON</button>
        </div>

        <div className="editor__actions">
          <button
            className="editor__save-btn"
            onClick={save}
            disabled={saving || !dirty}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          {contentType === "insights" && (
            <button
              className="editor__push-btn"
              onClick={handleSendPushClick}
              disabled={pushing || dirty}
            >
              {pushing ? "Sending..." : "Send Push"}
            </button>
          )}
          <button className="editor__delete-btn" onClick={deleteDocument} title="Delete document">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Meta (above locales — shared across all languages, editor mode only) */}
      {docData.meta && (
        <div className="editor__meta">
          <div className="editor__meta-header">
            <span className="editor__meta-label">Meta</span>
            <label className="editor__meta-draft">
              <input
                type="checkbox"
                checked={!!docData.meta.draft}
                onChange={(e) => updateField("meta.draft", e.target.checked)}
              />
              Do not publish
            </label>
          </div>
          <div className="editor__meta-body">
            <div className="editor__meta-thumb-row">
              {contentType !== "mealplans" && (
                <MetaThumb
                  value={docData.meta?.thumb || ""}
                  project={project!}
                  contentType={contentType!}
                  onChange={(url) => updateField("meta.thumb", url)}
                />
              )}
              <div className="editor__meta-fields">
                {/* ═══ INSIGHTS META ═══ */}
                {contentType !== "recipes" && (
                  <>
                    {/* Date + Category — same row */}
                    <div className="editor__meta-row">
                      {"date" in docData.meta && (
                        <div className="editor__meta-field">
                          <label>Date</label>
                          <input
                            type="date"
                            value={docData.meta.date || ""}
                            onChange={(e) => updateField("meta.date", e.target.value)}
                          />
                        </div>
                      )}
                      {"category" in docData.meta && (
                        <div className="editor__meta-field">
                          <label>Category</label>
                          {contentType === "workouts" ? (
                            <input
                              type="text"
                              value={docData.meta.category || ""}
                              onChange={(e) => updateField("meta.category", e.target.value)}
                              placeholder="e.g. 🏋️ Lower Body"
                            />
                          ) : (
                            <select
                              className="editor__meta-select"
                              value={selectedPrimaryCategoryValue}
                              onChange={(e) => handlePrimaryCategoryChange(e.target.value)}
                            >
                              <option value="">—</option>
                              {categorySelectOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    {(categories || []).length > 0 && (() => {
                      const selected: string[] = Array.isArray(docData.meta.tags) ? docData.meta.tags : [];
                      const toggle = (tag: string) => {
                        const next = selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag];
                        updateField("meta.tags", next);
                      };
                      return (
                        <div className="editor__meta-field editor__meta-field--full">
                          <label>Related categories</label>
                          <div className="editor__meta-tags">
                            {(categories || []).map((cat: any) => (
                              <button
                                key={cat.id}
                                className={`editor__meta-tag${selected.includes(cat.id) ? " editor__meta-tag--active" : ""}`}
                                onClick={() => toggle(cat.id)}
                              >
                                {cat.emoji} {deloc(cat.title)}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Other fields */}
                    {metaKeys.filter((k) => !["thumb", "date", "category", "tags", "draft", "days", "days_count", "has_shopping_list", "image"].includes(k)).map((key) => {
                      const val = docData.meta[key];
                      const isArray = Array.isArray(val);
                      const isMultiline = key === "description";
                      return (
                        <div key={key} className={`editor__meta-field${isMultiline ? " editor__meta-field--full" : ""}`}>
                          <label>{key.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}</label>
                          {isMultiline ? (
                            <textarea
                              className="editor__meta-textarea"
                              value={val || ""}
                              onChange={(e) => updateField(`meta.${key}`, e.target.value)}
                              rows={3}
                            />
                          ) : (
                            <input
                              type="text"
                              value={isArray ? val.join(", ") : (val || "")}
                              onChange={(e) => {
                                if (isArray) {
                                  updateField(`meta.${key}`, e.target.value.split(",").map((t: string) => t.trim()).filter(Boolean));
                                } else {
                                  updateField(`meta.${key}`, e.target.value);
                                }
                              }}
                              placeholder={isArray ? "comma, separated" : ""}
                            />
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* ═══ RECIPES META ═══ */}
                {contentType === "recipes" && (
                  <>
                    {/* Meal Type (multi) */}
                    <div className="editor__meta-field editor__meta-field--full">
                      <label>Meal Type</label>
                      {(() => {
                        const selectedFromCategory = Array.isArray(docData.meta?.category)
                          ? docData.meta.category
                          : (docData.meta?.category ? [docData.meta.category] : []);
                        const selected = selectedFromCategory.length > 0
                          ? selectedFromCategory
                          : (Array.isArray(docData.meta?.tags) ? docData.meta.tags : []);
                        const uniqueSelected = Array.from(new Set(selected.filter(Boolean)));
                        const toggleMealType = (id: string) => {
                          const next = uniqueSelected.includes(id)
                            ? uniqueSelected.filter((x) => x !== id)
                            : [...uniqueSelected, id];
                          updateField("meta.category", next);
                          updateField("meta.tags", next);
                        };
                        return (
                          <div className="editor__meta-tags">
                            {(categories || []).length > 0 ? (
                              (categories || []).map((cat: any) => (
                                <button
                                  key={cat.id}
                                  className={`editor__meta-tag${uniqueSelected.includes(cat.id) ? " editor__meta-tag--active" : ""}`}
                                  onClick={() => toggleMealType(cat.id)}
                                >
                                  {cat.emoji} {deloc(cat.title)}
                                </button>
                              ))
                            ) : (
                              <span className="editor__meta-tags-hint">No categories</span>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Meal Role — single select */}
                    {recipeOptions && "meal_role" in docData.meta && (() => {
                      const current = docData.meta.meal_role || "";
                      const options = recipeOptions.meal_roles || [];
                      return (
                        <div className="editor__meta-field editor__meta-field--full">
                          <label>Meal Role</label>
                          <select
                            className="editor__meta-select"
                            value={current}
                            onChange={(e) => updateField("meta.meal_role", e.target.value || null)}
                          >
                            <option value="">—</option>
                            {options.map((opt: any) => (
                              <option key={opt.id} value={opt.id}>{opt.title}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })()}

                    {/* Difficulty — chips */}
                    {recipeOptions && "difficulty" in docData.meta && (() => {
                      const current = docData.meta.difficulty || "";
                      const options = recipeOptions.difficulties || [];
                      return (
                        <div className="editor__meta-field editor__meta-field--full">
                          <label>Difficulty</label>
                          <div className="editor__meta-tags">
                            {options.map((opt: any) => (
                              <button
                                key={opt.id}
                                className={`editor__meta-tag${current === opt.id ? " editor__meta-tag--active" : ""}`}
                                onClick={() => updateField("meta.difficulty", current === opt.id ? null : opt.id)}
                              >
                                {opt.title}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Numeric fields — compact grid */}
                    {(() => {
                      const LABEL_MAP: Record<string, string> = {
                        servings: "Servings",
                        prep_time_minutes: "Prep (min)",
                        cook_time_minutes: "Cook (min)",
                        serving_weight_grams: "Serving (g)",
                        calories_per_serving_kcal: "Cal/serving",
                      };
                      const fields = metaKeys.filter((k) =>
                        !["thumb", "category", "tags", "meal_role", "difficulty"].includes(k)
                      );
                      if (!fields.length) return null;
                      return (
                        <div className="editor__meta-grid">
                          {fields.map((key) => {
                            const val = docData.meta[key];
                            const isNum = typeof val === "number";
                            const label = LABEL_MAP[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
                            return (
                              <div key={key} className="editor__meta-field">
                                <label>{label}</label>
                                <input
                                  type={isNum ? "number" : "text"}
                                  value={val ?? ""}
                                  onChange={(e) => {
                                    if (isNum) {
                                      updateField(`meta.${key}`, e.target.value === "" ? null : Number(e.target.value));
                                    } else {
                                      updateField(`meta.${key}`, e.target.value);
                                    }
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {containsList.length > 0 && (
                      <div className="editor__meta-field editor__meta-field--full">
                        <label>Contains</label>
                        <div className="editor__meta-contains">{containsList.join(", ")}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Locale tabs */}
      <div className="editor__locale-tabs">
          {LOCALES.map((loc) => (
            <button
              key={loc}
              className={`editor__locale-tab${locale === loc ? " editor__locale-tab--active" : ""}${!hasLocaleContent(docData.content || [], loc) ? " editor__locale-tab--missing" : ""}`}
              onClick={() => setLocale(loc)}
            >
              {loc.toUpperCase()}
            </button>
          ))}
          <button
            className="editor__translate-btn"
            onClick={handleTranslate}
            disabled={translating}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/>
              <path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/>
            </svg>
            {translating
              ? `${translateProgress} / ${translateTotal}`
              : locale === "en"
                ? "Translate All"
                : `Translate to ${locale.toUpperCase()} ${LOCALE_FLAGS[locale] ?? ""}`}
          </button>
        </div>

      {/* Contains — always visible as inline tags */}
      {contentType === "recipes" && containsList.length > 0 && (
        <div className="editor__recipe-contains">
          <span className="editor__recipe-contains-label">Contains</span>
          {containsList.map((item) => (
            <span key={item} className="editor__recipe-contains-tag">{item}</span>
          ))}
        </div>
      )}

      {/* Recipe data preview (read-only, shows what goes to export) */}
      {contentType === "recipes" && docData && (
        <div className="editor__recipe-data">
          {/* Ingredients */}
          {docData.ingredients_raw?.length > 0 && (
            <details className="editor__recipe-section" open>
              <summary className="editor__recipe-section-title">Ingredients</summary>
              <div className="editor__recipe-section-body">
                {docData.ingredients_raw.map((group: any, gi: number) => {
                  const resolveLoc = (v: any) => {
                    if (v && typeof v === "object" && !Array.isArray(v)) return v[locale] || v.en || "";
                    return v ?? "";
                  };
                  return (
                  <div key={gi}>
                    {group.title && <div className="editor__recipe-group-title">{resolveLoc(group.title)}</div>}
                    {(group.items || []).map((item: any, ii: number) => {
                      const m = item.display_unit?.metric;
                      const imp = item.display_unit?.imperial;
                      return (
                        <div key={ii} className="editor__recipe-ingredient">
                          <span className="editor__recipe-ingredient-emoji">{item.emoji || ""}</span>
                          <span className="editor__recipe-ingredient-name">{resolveLoc(item.name)}</span>
                          <span className="editor__recipe-ingredient-amount">
                            {m ? `${m.amount} ${m.unit}` : `${item.quantity} ${item.unit}`}
                            {imp ? ` / ${imp.amount} ${imp.unit}` : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  );
                })}
              </div>
            </details>
          )}

          {/* Nutrition */}
          {docData.nutrition && (
            <details className="editor__recipe-section">
              <summary className="editor__recipe-section-title">Nutrition</summary>
              <div className="editor__recipe-section-body">
                {docData.nutrition.per_serving && (
                  <div className="editor__recipe-nutrition-block">
                    <div className="editor__recipe-nutrition-label">Per Serving</div>
                    <div className="editor__recipe-nutrition-grid">
                      {Object.entries(docData.nutrition.per_serving)
                        .filter(([k]) => !k.startsWith("%"))
                        .map(([k, v]) => (
                          <div key={k} className="editor__recipe-nutrition-item">
                            <span>{k.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}</span>
                            <span>{String(v)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                {docData.nutrition.per_100g && (
                  <div className="editor__recipe-nutrition-block">
                    <div className="editor__recipe-nutrition-label">Per 100g</div>
                    <div className="editor__recipe-nutrition-grid">
                      {Object.entries(docData.nutrition.per_100g)
                        .filter(([k]) => !k.startsWith("%"))
                        .map(([k, v]) => (
                          <div key={k} className="editor__recipe-nutrition-item">
                            <span>{k.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}</span>
                            <span>{String(v)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </details>
          )}

        </div>
      )}

      {/* Main area */}
      {viewMode === "preview" ? (
        <div className="editor__body editor__body--simple">
          <div className="simple__content">
            {content.length === 0 ? (
              <div className="editor__empty-content">
                <p>No content yet. Switch to Editor mode to add blocks.</p>
              </div>
            ) : (
              <>
                {content.map((block: any, index: number) => (
                  <SimpleBlock
                    key={index}
                    block={block}
                    locale={locale}
                    project={project!}
                    contentType={contentType!}
                    onUpdate={(b) => updateBlock(index, b)}
                    onDelete={() => deleteBlock(index)}
                    blockConfig={blockConfig || undefined}
                    blockPath={[index]}
                    onAutoSaveImage={autoSaveImageUrl}
                  />
                ))}
                {contentType === "workouts" && (
                  <button className="workout-card__add" onClick={() => addBlock("card")}>+ Add exercise</button>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="editor__body">
            {/* Block list with drag & drop */}
            <div className="editor__blocks-area">
              <SortableContext items={rootIds} strategy={verticalListSortingStrategy}>
                <BlocksDropZone active={hoverRoot}>
                  {content.length === 0 ? (
                    <div className="editor__empty-content">
                      <p>Drag blocks from the panel on the right.</p>
                    </div>
                  ) : (
                    content.map((block: any, index: number) => (
                      <React.Fragment key={rootIds[index]}>
                        <DropGap id={`gap-${index}`} />
                        <SortableBlock id={rootIds[index]}>
                          <BlockWrapper
                            block={block}
                            index={index}
                            total={content.length}
                            blockPath={[index]}
                            project={project!}
                            contentType={contentType!}
                            locale={locale}
                            isDropTarget={(block.type === "group" || Array.isArray(block.content)) && hoverGroupPath === String(index)}
                            hoverGroupPath={hoverGroupPath}
                            onUpdate={(newBlock) => updateBlock(index, newBlock)}
                            onDelete={() => deleteBlock(index)}
                            blockConfig={blockConfig || undefined}
                          />
                        </SortableBlock>
                      </React.Fragment>
                    )).concat(
                      <DropGap key="gap-last" id={`gap-${content.length}`} />
                    )
                  )}
                </BlocksDropZone>
              </SortableContext>
            </div>

            {/* Block palette sidebar */}
            <aside className="editor__palette">
              <div className="editor__palette-header">Add block</div>
              <div className="editor__palette-list">
                {(blockConfig || []).map((b: any) => (
                  <DraggablePaletteItem key={b.type} type={b.type} label={b.label} />
                ))}
                {(!blockConfig || blockConfig.length === 0) && (
                  <div className="editor__palette-empty">Set up blocks in General tab</div>
                )}
              </div>
            </aside>
          </div>

          <DragOverlay>
            {activeBlock && (
              <div className="editor__drag-overlay">
                <span className="editor__drag-overlay-type">{activeBlock.type}</span>
                <span className="editor__drag-overlay-text">
                  {getBlockPreview(activeBlock, locale)}
                </span>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Translation overlay */}
      {translating && (
        <div className="translate-overlay">
          <div className="translate-overlay__card">
            <div className="translate-overlay__icon">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                <path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/>
                <path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/>
              </svg>
            </div>
            <p className="translate-overlay__counter">{translateProgress} / {translateTotal}</p>
            <div className="translate-overlay__track">
              <div
                className="translate-overlay__fill"
                style={{ width: `${translateTotal ? (translateProgress / translateTotal) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}
      {showPushTime && (
        <div className="push-dialog__overlay" onClick={handlePushCancel}>
          <div className="push-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="push-dialog__title">Schedule Push</h3>
            <p className="push-dialog__desc">Push will fire at the selected time in each user's local timezone</p>
            <div className="push-dialog__env">
              <button
                className={`push-dialog__env-btn ${pushEnv === "dev" ? "push-dialog__env-btn--active" : ""}`}
                onClick={() => setPushEnv("dev")}
              >Stage</button>
              <button
                className={`push-dialog__env-btn push-dialog__env-btn--prod ${pushEnv === "prod" ? "push-dialog__env-btn--active" : ""}`}
                onClick={() => setPushEnv("prod")}
              >Prod</button>
            </div>
            <input
              type="time"
              className="push-dialog__time"
              value={pushTime}
              onChange={(e) => setPushTime(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && pushTime) handlePushConfirm(); }}
              autoFocus
            />
            <div className="push-dialog__actions">
              <button className="push-dialog__cancel" onClick={handlePushCancel}>Cancel</button>
              <button className="push-dialog__send" onClick={handlePushConfirm} disabled={!pushTime}>Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Editor;
